import { ActionTree } from 'vuex';
import httpClient from '@/httpClient/httpClient';
import ResponseDataValue, {
  ResponseDataProviderValue,
} from '@/utils/ResponseDataValue';
import { Notification, MessageBox } from 'uiv';
import i18n from '@/i18n';
import { clone } from 'ramda';
import { getAllIdsByProp } from '@/utils/normalizeData';
import {
  setAudienceErrorsFromResponseData,
  validateAudienceForm,
} from './utils/validateAudienceForm';
import {
  ResponseBody,
  ResponseData,
  ResponseDataProvider,
  ResponseError,
  RootState,
  TSubmitData,
} from '@/types';
import { Audience, AudienceState, ESource } from '@/types/audience/audience';

const REQUEST_ERROR = i18n.t('notifications.errorRequest');
const DELETE_SUCCESS = i18n.t('notifications.deleteSuccess');
const HOME_PAGE = '/audiences';
const getActions = (endpoints = {}) => ({
  ...endpoints,

  // ADD
  setAudience({ commit, getters }, data) {
    const audienceInit = clone(getters.getAudienceInit) as Audience;

    const audience: Audience = {
      name: data.name ?? audienceInit.name,
      source: data.source ?? audienceInit.source,
      pixelIds: getAllIdsByProp('pixels', data) ?? audienceInit.pixelIds,
      siteTimeId: data.site_time_id ?? audienceInit.siteTimeId,
      fromDate: data.from_date ?? audienceInit.fromDate,
      toDate: data.to_date ?? audienceInit.toDate,
      fileName: audienceInit.fileName,
      fileData: audienceInit.fileData,
      errors: audienceInit.errors,
    };

    commit('setAudience', audience);
  },

  // UPDATE
  updateAudienceData({ commit }, payload) {
    commit('updateAudience', payload);
  },
  clearAudienceData({ commit }) {
    commit('clearAudience');
  },

  // SUBMIT FORM
  async submitForm({ commit, getters, dispatch }, submitData: TSubmitData) {
    const audience = getters.getAudience;
    const audienceValidate = validateAudienceForm(audience);

    if (!audienceValidate) {
      Notification.notify.warning({
        title: i18n.t('notifications.checkForm'),
        duration: 3000,
      });
      return;
    }

    commit('changeIsSavingForm', true);
    const formData = new FormData();
    if (audience.name) {
      formData.append('name', audience.name);
    }
    if (audience.source) {
      formData.append('source', audience.source);
    }

    if (audience.source === ESource.VISITORS) {
      if (audience.pixelIds.length) {
        formData.append('pixel_ids[]', audience.pixelIds[0]?.toString());
      }
      if (audience.siteTimeId) {
        formData.append('site_time_id', audience.siteTimeId.toString());
      }
      if (audience.fromDate) {
        formData.append('from_date', audience.fromDate.toString());
      }
      if (audience.toDate) {
        formData.append('to_date', audience.toDate.toString());
      }
    }

    if (audience.source === ESource.CUSTOMERS) {
      if (audience.fileData !== null) {
        formData.append('file_data', audience.fileData);
      }
    }

    const { APIName, id, backUrl } = submitData;

    const response: ResponseData = await dispatch(APIName, {
      formData,
      id,
    });

    commit('changeIsSavingForm', false);

    if (response?.success) {
      const sspUserIdCount = response.ssp_user_id_count;

      if (sspUserIdCount) {
        MessageBox.alert({
          title: i18n.t('notifications.attantion'),
          content: i18n.t('notifications.addedUsers', {
            count: sspUserIdCount,
            filename: audience.fileName,
          }),
          okText: i18n.t('body.popup.buttons.ok'),
        }).then(() => {
          window.location.href = backUrl ?? HOME_PAGE;
        });
        return;
      }

      window.location.href = backUrl ?? HOME_PAGE;
    } else {
      const errors = response?.data;
      setAudienceErrorsFromResponseData(errors, audience);
    }

    return response;
  },
});

// API
const endpoints = {
  async audience({ dispatch }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/audience/' + data.id,
        {
          params: data,
        }
      );

      dispatch('setAudience', response.data);
      return ResponseDataValue(response.data, true);
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },

  async createAudience(_, data) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/audience',
        data.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      const error = e as ResponseError;
      return ResponseDataValue(error.response.data, false);
    }
  },

  async updateAudience(_, data) {
    try {
      const response: ResponseBody = await httpClient().put(
        '/audience/' + data.id,
        data.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      const error = e as ResponseError;
      return ResponseDataValue(error.response.data, false);
    }
  },

  async deleteAudience({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().delete(
        '/audience/' + data.id
      );
      commit('deleteAudience', data.id);
      Notification.notify.success({
        title: DELETE_SUCCESS,
      });
      return ResponseDataValue(response.data, true);
    } catch (e) {
      const error = e as ResponseError;
      return ResponseDataValue(error.response.data, false);
    }
  },

  async audiencesList({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get('/audience', {
        params: Object.assign({}, data.searchData, {
          expand: 'pixels',
          per_page: 0,
          fields: data.columns.join(','),
        }),
      });
      const responseDataProvider: ResponseDataProvider = ResponseDataProviderValue(
        response,
        true
      );
      commit('setAudiencesList', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },

  async getPixels({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get('/pixel', {
        params: data,
      });
      commit('setPixel', response.data);
      return ResponseDataValue(response.data, true);
    } catch (e) {
      const error = e as ResponseError;
      return ResponseDataValue(error.response.data, false);
    }
  },

  async createPixel({ commit }, formData) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/pixel',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      commit('updatePixel', response.data);
      return ResponseDataValue(response.data, true);
    } catch (e) {
      const error = e as ResponseError;
      return ResponseDataValue(error.response.data, false);
    }
  },

  async deletePixel({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().delete(
        '/pixel/' + data.pixel_id + '/audience/' + data.audience_id
      );
      commit('deletePixel', data.pixel_id);
      return ResponseDataValue(response.data, true);
    } catch (e) {
      const error = e as ResponseError;
      return ResponseDataValue(error.response.data, false);
    }
  },

  async bindPixelToAudience(_, data) {
    try {
      const response: ResponseBody = await httpClient().put(
        '/pixel/' + data.pixel_id + '/audience/' + data.audience_id,
        data,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      const error = e as ResponseError;
      return ResponseDataValue(error.response.data, false);
    }
  },

  async siteTimes({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/dictionary/site-time',
        {
          params: Object.assign({}, data),
        }
      );
      const responseDataProvider: ResponseDataProvider = ResponseDataProviderValue(
        response,
        true
      );
      commit('setSiteTimes', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
};

const actions: ActionTree<AudienceState, RootState> = getActions(endpoints);

export { getActions, endpoints };
export default actions;
